import { cloneElement, useMemo } from "react";

import { BackgroundProps, ColorProps, Flex, Box, useDisclosure } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import { InventoryChangeReason } from "__graphql__/types";
import { EppoFeatureFlags } from "app/types/flags";
import { transformChunks } from "shared/components/TextWrapper";
import { useEppoFeatureFlagProvider } from "shared/hooks/useEppoFeatureFlag";
import { Accordion } from "ui/Accordion/Accordion";
import { BottomSheet } from "ui/BottomSheet/BottomSheet";
import { Button } from "ui/Button/Button";
import { DropDown } from "ui/DropDown/DropDown";
import { DropDownOption } from "ui/DropDown/DropDownList";
import { InformationIcon } from "ui/Icons/Icons";
import { IncreaseDecrease } from "ui/IncreaseDecrease/IncreaseDecrease";
import { HeaderM, TitleM, TitleS, BodyS, DetailL } from "ui/Typography/Typography";

import { reasonMetadata } from "../utils/constants";

type StockCounterProps = {
  numberColor?: ColorProps["color"];
  bgColor?: BackgroundProps["bg"];
  value: number;
  title: string;
  countDataTestId?: string;
};

function StockCounter({ numberColor, bgColor, value, title, countDataTestId }: StockCounterProps) {
  return (
    <Flex direction="column" alignItems="center">
      <TitleS color="grey.600">{title}</TitleS>
      <HeaderM
        data-testid={countDataTestId}
        paddingX="s100"
        borderRadius="8px"
        bg={bgColor}
        color={numberColor}
        mt="s50"
      >
        {value}
      </HeaderM>
    </Flex>
  );
}

const ALLOWED_REASONS = [
  InventoryChangeReason.outbound_product_damaged,
  InventoryChangeReason.outbound_product_expired,
  InventoryChangeReason.outbound_too_good_to_go,
  InventoryChangeReason.correction_break_in,
  InventoryChangeReason.outbound_equipment_failure,
  InventoryChangeReason.product_recall,
  InventoryChangeReason.correction,
];

const BLOCKED_REASONS_IN_EOY: InventoryChangeReason[] = [
  InventoryChangeReason.correction,
  InventoryChangeReason.correction_break_in,
];

export function StockAdjustmentPanel({
  initialStock,
  newStock,
  isIncreaseBtnDisabled,
  selectedReason,
  setSelectedReason,
  counter,
  setCounter,
}: {
  initialStock: number;
  newStock: number;
  isIncreaseBtnDisabled: boolean;
  selectedReason: string | null;
  setSelectedReason: (value: InventoryChangeReason | null) => void;
  counter: number;
  setCounter: (value: number) => void;
}) {
  const intl = useIntl();
  const { isFeatureEnabled: enableNewCorrectionReason } = useEppoFeatureFlagProvider(
    EppoFeatureFlags.ENABLE_NEW_STOCK_CORRECTION_REASON,
  );
  const { isFeatureEnabled: blockCorrectionsInEOY } = useEppoFeatureFlagProvider(
    EppoFeatureFlags.EOY_HIDE_CORRECTIONS,
  );

  const {
    isOpen: isBottomSheetOpen,
    onOpen: openBottomSheet,
    onClose: closeBottomSheet,
  } = useDisclosure();

  const allowedReasons = useMemo<InventoryChangeReason[]>(() => {
    let reasons = [...ALLOWED_REASONS];

    if (enableNewCorrectionReason) {
      reasons.splice(6, 0, InventoryChangeReason.correction_baking_ingredients);
    }

    if (blockCorrectionsInEOY) {
      reasons = reasons.filter((r) => !BLOCKED_REASONS_IN_EOY.includes(r));
    }

    return reasons;
  }, [blockCorrectionsInEOY, enableNewCorrectionReason]);

  const options = useMemo<DropDownOption[]>(() => {
    return allowedReasons.map((reason) => {
      const reasonMeta = reasonMetadata[reason];
      return {
        key: reason,
        label: intl.formatMessage({ id: reasonMeta.label }),
        icon: reasonMeta.icon,
      };
    });
  }, [intl, allowedReasons]);

  const accordionInfo = useMemo(() => {
    return allowedReasons.map((reason) => {
      const reasonMeta = reasonMetadata[reason];
      return {
        key: reason,
        icon: reasonMeta.icon,
        explanationTitle: intl.formatMessage({ id: reasonMeta.explanationTitleId }),
        explanationDescription: intl.formatMessage(
          { id: reasonMeta.explanationDescriptionId },
          { ...transformChunks({ b: { as: "strong" } }), br: <br /> },
        ),
      };
    });
  }, [intl, allowedReasons]);

  const formattedCounter = useMemo(() => {
    return selectedReason && selectedReason !== InventoryChangeReason.correction
      ? -counter
      : counter;
  }, [selectedReason, counter]);

  const countLabelId = useMemo(() => {
    return selectedReason ? reasonMetadata[selectedReason]?.countLabelId : undefined;
  }, [selectedReason]);
  const isCorrection = selectedReason === InventoryChangeReason.correction;

  const reasonIndexesOpenedByDefault = useMemo(() => {
    if (selectedReason) {
      return [allowedReasons.findIndex((r) => r === selectedReason)];
    }
    return [0, 1, 2];
  }, [selectedReason, allowedReasons]);

  return (
    <>
      <Flex
        alignItems="center"
        justifyContent="space-between"
        borderRadius="8px"
        bg="white"
        w="100%"
        px="s300"
        py="s200"
      >
        <StockCounter
          title={intl.formatMessage({ id: "inventory-change-reason.counter.stock" })}
          value={initialStock}
        />
        <StockCounter
          title={intl.formatMessage({
            id: "inventory-change-reason.counter.change",
          })}
          value={formattedCounter}
          numberColor="purple.500"
        />
        <StockCounter
          title={intl.formatMessage({ id: "inventory-change-reason.counter.new-stock" })}
          value={newStock}
          numberColor="pinkFlink.500"
          bgColor="pinkFlink.100"
        />
      </Flex>
      <Flex direction="column" bg="white" borderRadius="8px" p="s200">
        <TitleM color="grey.800">
          {intl.formatMessage({ id: "inventory-change-reason.correction-reason.title" })}
        </TitleM>
        <Button
          variant="ghost"
          size="xs"
          p="0"
          m="0"
          mb="s100"
          bg="transparent"
          onClick={openBottomSheet}
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
          gap="s50"
          _hover={{ bg: "transparent" }}
          _active={{ bg: "transparent" }}
        >
          <DetailL color="blue.500">
            {intl.formatMessage({
              id: "inventory-change-reason.reasons_explanations_title",
            })}
          </DetailL>
          <Box w="18px" h="18px" display="flex" alignItems="center" justifyContent="center">
            <InformationIcon color="blue.500" boxSize="15px" />
          </Box>
        </Button>
        <DropDown
          options={options}
          value={selectedReason}
          onInput={(v) => {
            setSelectedReason(v as InventoryChangeReason);
            setCounter(0);
          }}
          filterMode={false}
          border="1px solid"
          borderColor="grey.600"
          borderRadius="8px"
          bgColor="white"
          titleColor="grey.800"
          color="grey.800"
          w="100%"
          h="40px"
          placement="top"
          showScrollbar
          listStyles={{ w: "300px", h: "300px", mr: "0px" }}
          listItemStyles={{ w: "300px" }}
        >
          {intl.formatMessage({
            id: "inventory-change-reason.correction-reason.droppdown-placeholder",
          })}
        </DropDown>
        {selectedReason && countLabelId && (
          <IncreaseDecrease
            changeHandler={setCounter}
            label={
              countLabelId ? (
                <FormattedMessage
                  id={countLabelId}
                  values={transformChunks({ b: { as: "strong" } })}
                />
              ) : undefined
            }
            count={counter}
            isIncreaseBtnDisabled={!isCorrection && isIncreaseBtnDisabled}
            isDecreaseBtnDisabled={isCorrection && isIncreaseBtnDisabled}
            allowNegativeInput={isCorrection}
            withInput
          />
        )}
      </Flex>
      <BottomSheet isOpen={isBottomSheetOpen} onClose={closeBottomSheet}>
        <Accordion allowMultiple defaultIndex={reasonIndexesOpenedByDefault}>
          {accordionInfo.map((option) => (
            <Accordion.Item key={option.key}>
              <Accordion.ItemTrigger>
                {option.icon && cloneElement(option.icon, { boxSize: "24px" })}
                <BodyS mx="s100">{option.explanationTitle}</BodyS>
                <Accordion.ItemIndicator />
              </Accordion.ItemTrigger>
              <Accordion.ItemContent>
                <DetailL>{option.explanationDescription}</DetailL>
              </Accordion.ItemContent>
            </Accordion.Item>
          ))}
        </Accordion>
      </BottomSheet>
    </>
  );
}
